import React, { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { DeliveryLocation } from "../deliveryLocationsTypes"
import {
    MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATION_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
    getEditDeliveryLocationRoute,
} from "../../../../../constants/routes"
import {
    DeliveryLocationsQuery,
    DeliveryLocationsResponse,
    deleteDeliveryLocation,
    fetchDeliveryLocations,
} from "../deliveryLocationsApi"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}
const selectDeliveryLocations = (data: DeliveryLocationsResponse) => data?.deliveryLocations || []

export function DeliveryLocations() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const DeliveryLocationsResult = useApiQuery(fetchDeliveryLocations, {
        queryName: DeliveryLocationsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectDeliveryLocations,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(DeliveryLocationsResult)

    const columns = useMemo<MRT_ColumnDef<DeliveryLocation>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-delivery-locations-table-header-name"),
                accessorKey: "displayName",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-delivery-locations-table-header-webshop"),
                accessorFn: (row) => row.webshop?.name ?? "",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("merchant-delivery-locations-table-header-status"),
                },

                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeliveryLocation } = useApiMutation(deleteDeliveryLocation, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [DeliveryLocationsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeliveryLocation({ deliveryLocationId: confirmModalState.itemId })
        } else {
            throw new Error("Delivery Location id is undefined")
        }
    }, [confirmModalState.itemId, callDeliveryLocation])

    return (
        <>
            <LocalizedStrict id="merchant-delivery-locations-header" attrs={{ title: true }}>
                <PageHeader
                    title="Delivery locations"
                    page={Page.MERCHANT_ADMIN_DELIVERY_LOCATIONS}
                    rightElement={
                        <LocalizedStrict id="merchant-delivery-locations-create-button-link">
                            <StandardButton
                                onClick={() => navigate(MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATION_CREATE_ROUTE)}
                            >
                                Create delivery location
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-delivery-locations">
                            <Breadcrumb>Delivery locations</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                navigate(getEditDeliveryLocationRoute(row.original.id))
                            }}
                            key={`edit-${row.original.id}`}
                        >
                            <LocalizedStrict id="merchant-delivery-locations-table-button-edit">
                                <>Edit</>
                            </LocalizedStrict>
                        </MenuItem>,

                        <DestructiveMenuItem
                            onClick={() => {
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    confirmModalText: `${row.original.displayName}`,
                                    itemId: row.original.id,
                                })
                                closeMenu()
                            }}
                            key={`delete-${row.original.id}`}
                        >
                            <LocalizedStrict id="merchant-delivery-locations-table-button-delete">
                                <>Delete</>
                            </LocalizedStrict>
                        </DestructiveMenuItem>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <LocalizedStrict id="merchant-delivery-locations-delete-entity-dialog" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="delivery location"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
